import { PlanItemModel, ShiftModel } from '@/libs/Api';
import { graphqlPost } from '@/libs/tpm-analytics/graphql/transport';
import { toIsoString } from '@/libs/tpm-analytics/graphql/queries';

function listExpr(ids?: string[]): string {
    return ids && ids.length > 0 ? `[${ids.map((x) => `"${x}"`).join(', ')}]` : '[]';
}

function buildWhereShifts(filters?: any): string {
    const parts: string[] = [
        // overlaps interval: shift.from < to AND shift.to > from
        // Používáme striktní nerovnosti kvůli výrobnímu dni (konec výrobního dne je začátek následujícího)
        `fromDate: { lt: $to }`,
        `toDate: { gt: $from }`,
    ];
    const f = filters || {};
    if (f.workplaceIds && f.workplaceIds.length > 0) parts.push(`workplaceId: { in: ${listExpr(f.workplaceIds)} }`);
    if (f.locationIds && f.locationIds.length > 0) parts.push(`workplace: { locationId: { in: ${listExpr(f.locationIds)} } }`);
    if (f.projectIds && f.projectIds.length > 0) parts.push(`shiftProductions: { some: { projectId: { in: ${listExpr(f.projectIds)} } } }`);
    if (f.shiftTypeIds && f.shiftTypeIds.length > 0) parts.push(`shiftTypeId: { in: ${listExpr(f.shiftTypeIds)} }`);
    if (f.shiftTeamTypeIds && f.shiftTeamTypeIds.length > 0) parts.push(`shiftTeamTypeId: { in: ${listExpr(f.shiftTeamTypeIds)} }`);
    return `{ ${parts.join(', ')} }`;
}

function buildWherePlanItems(isPlanned: boolean, filters?: any): string {
    const parts: string[] = [
        `isWorkplaceShutdown: { eq: true }`,
        `auditFaultType: { isPlannedShutdown: { eq: ${isPlanned ? 'true' : 'false'} } }`,
        // overlaps interval - používáme striktní nerovnosti kvůli výrobnímu dni
        `shutdownFromDate: { lt: $to }`,
        // shutdownToDate může být null (neukončená odstávka) - musíme zahrnout i tyto případy
        `or: [{ shutdownToDate: { gt: $from } }, { shutdownToDate: { eq: null } }]`,
    ];
    const f = filters || {};
    if (f.workplaceIds && f.workplaceIds.length > 0) parts.push(`workplaceId: { in: ${listExpr(f.workplaceIds)} }`);
    if (f.locationIds && f.locationIds.length > 0) parts.push(`workplace: { locationId: { in: ${listExpr(f.locationIds)} } }`);
    if (!isPlanned && f.faultTypeIds && f.faultTypeIds.length > 0)
        parts.push(`auditFaultTypeId: { in: ${listExpr(f.faultTypeIds)} }`);
    return `{ ${parts.join(', ')} }`;
}

function buildOeeBatchQuery(whereShifts: string, whereUnplanned: string, wherePlanned: string, withPrev: boolean): string {
    const prevVars = withPrev ? `, $prevFrom: DateTime!, $prevTo: DateTime!` : '';
    const prevBlocks = withPrev
        ? `
            shiftsPrev: shifts(pageSize: 999999, where: ${whereShifts.replace('$from', '$prevFrom').replace('$to', '$prevTo')}) {
                id fromDate toDate okCount nokCount planCount workplaceId
                workplace { id code name location { id parentalLocationId } }
                shiftTypeId shiftType { id name } shiftTeamTypeId shiftTeamType { id name }
                shiftProductions { 
                    projectId 
                    project { id name } 
                    productId 
                    product { id name code productCollectionId } 
                    okCount 
                    nokCount 
                    shiftProductionRecords { 
                        id 
                        recordTime 
                        okCount 
                    }
                }
            }
            unplannedPrev: planItems(pageSize: 999999, where: ${whereUnplanned.replace('$from', '$prevFrom').replace('$to', '$prevTo')}) {
                id workplaceId shutdownFromDate shutdownToDate shutdownDuration reactionDuration auditFaultType { id name priority }
            }
            plannedPrev: planItems(pageSize: 999999, where: ${wherePlanned.replace('$from', '$prevFrom').replace('$to', '$prevTo')}) {
                id workplaceId shutdownFromDate shutdownToDate shutdownDuration reactionDuration auditFaultType { id name priority }
            }
        `
        : '';
    return `query($from: DateTime!, $to: DateTime!${prevVars}) {
        shiftsCurr: shifts(pageSize: 999999, where: ${whereShifts}) {
            id fromDate toDate okCount nokCount planCount workplaceId
            workplace { id code name location { id parentalLocationId } }
            shiftTypeId shiftType { id name } shiftTeamTypeId shiftTeamType { id name }
            shiftProductions { 
                projectId 
                project { id name } 
                productId 
                product { id name code productCollectionId } 
                okCount 
                nokCount 
                shiftProductionRecords { 
                    id 
                    recordTime 
                    okCount 
                }
            }
        }
        unplannedCurr: planItems(pageSize: 999999, where: ${whereUnplanned}) {
            id workplaceId shutdownFromDate shutdownToDate shutdownDuration reactionDuration auditFaultType { id name priority }
        }
        plannedCurr: planItems(pageSize: 999999, where: ${wherePlanned}) {
            id workplaceId shutdownFromDate shutdownToDate shutdownDuration reactionDuration auditFaultType { id name priority }
        }
        ${prevBlocks}
    }`;
}

export class OeeBatchRepository {
    static async fetchCurrent(
        fromIso?: string,
        toIso?: string,
        filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
    ): Promise<{ shifts: ShiftModel[]; unplanned: PlanItemModel[]; planned: PlanItemModel[] }> {
        const whereS = buildWhereShifts(filters);
        const whereU = buildWherePlanItems(false, filters);
        const whereP = buildWherePlanItems(true, filters);
        const query = buildOeeBatchQuery(whereS, whereU, whereP, false);
        const data = await graphqlPost<any>(query, { from: toIsoString(fromIso), to: toIsoString(toIso) });
        const result = {
            shifts: (data?.shiftsCurr as ShiftModel[]) || [],
            unplanned: (data?.unplannedCurr as PlanItemModel[]) || [],
            planned: (data?.plannedCurr as PlanItemModel[]) || [],
        };
        return result;
    }

    static async fetchCurrentAndPrev(
        fromIso: string,
        toIso: string,
        prevFromIso: string,
        prevToIso: string,
        filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
    ): Promise<{
        curr: { shifts: ShiftModel[]; unplanned: PlanItemModel[]; planned: PlanItemModel[] };
        prev: { shifts: ShiftModel[]; unplanned: PlanItemModel[]; planned: PlanItemModel[] };
    }> {
        const whereS = buildWhereShifts(filters);
        const whereU = buildWherePlanItems(false, filters);
        const whereP = buildWherePlanItems(true, filters);
        const query = buildOeeBatchQuery(whereS, whereU, whereP, true);
        const data = await graphqlPost<any>(query, {
            from: toIsoString(fromIso),
            to: toIsoString(toIso),
            prevFrom: toIsoString(prevFromIso),
            prevTo: toIsoString(prevToIso),
        });
        return {
            curr: {
                shifts: (data?.shiftsCurr as ShiftModel[]) || [],
                unplanned: (data?.unplannedCurr as PlanItemModel[]) || [],
                planned: (data?.plannedCurr as PlanItemModel[]) || [],
            },
            prev: {
                shifts: (data?.shiftsPrev as ShiftModel[]) || [],
                unplanned: (data?.unplannedPrev as PlanItemModel[]) || [],
                planned: (data?.plannedPrev as PlanItemModel[]) || [],
            },
        };
    }
}


